// Competition scope picker (C2c) — Ben's select/exclude PILL pattern for the
// SUBJECT player's own data scope on a stats card. A chip shows the current
// scope; clicking it opens a value-picker where each competition can be either
// SELECTED (green — "just this one") or EXCLUDED (red X — "everything except
// this one"), plus an "All competitions" reset. The three choices map to the
// pure CompetitionScope (all / one / except); resolveCompetitionScope turns that
// into the competition_id[] the widget queries.
//
// Kept deliberately close-to-fidelity, not pixel-perfect (Ben refines later):
// chip colours by mode (neutral All / green one / red except), a filter box, and
// a per-row label(select) + red-X(exclude). Closes on outside click or a choice.

import { useEffect, useMemo, useRef, useState } from "react";
import { competitionChipLabel, type CompetitionScope } from "../competitionScope.js";
import { theme } from "../theme.js";

interface Competition {
  id: number;
  name: string;
}

export function CompetitionScopePicker({
  competitions,
  scope,
  onChange,
}: {
  competitions: Competition[];
  scope: CompetitionScope;
  onChange: (scope: CompetitionScope) => void;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on any click outside the picker.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? competitions.filter((c) => c.name.toLowerCase().includes(q)) : competitions;
  }, [competitions, filter]);

  const label = competitionChipLabel(scope, competitions);
  // Chip colour by mode — neutral for All, green for one, red for except.
  const chip =
    scope.mode === "one"
      ? { bg: theme.accentSurface, border: theme.accent, color: theme.accent }
      : scope.mode === "except"
        ? { bg: "rgba(231,76,60,0.15)", border: theme.danger, color: theme.danger }
        : { bg: theme.field, border: theme.border, color: theme.inkMuted };

  function choose(next: CompetitionScope) {
    onChange(next);
    setOpen(false);
    setFilter("");
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", marginTop: 4 }}>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setOpen((o) => !o)}
        title="Competition scope for this player"
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: chip.bg,
          color: chip.color,
          border: `1px solid ${chip.border}`,
          borderRadius: 12,
          fontSize: 10,
          padding: "2px 8px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label} ▾
      </button>

      {open ? (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            minWidth: 180,
            maxWidth: 240,
            zIndex: 30,
            background: theme.tileHeader,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            overflow: "hidden",
          }}
        >
          {competitions.length > 6 ? (
            <input
              autoFocus
              placeholder="Filter competitions…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: theme.field,
                border: "none",
                borderBottom: `1px solid ${theme.border}`,
                color: theme.ink,
                fontSize: 11,
                padding: "6px 8px",
                outline: "none",
              }}
            />
          ) : null}

          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {/* All competitions — the reset. */}
            <button
              type="button"
              onClick={() => choose({ mode: "all" })}
              style={optionStyle(scope.mode === "all" ? theme.accent : theme.inkMuted)}
            >
              All competitions
            </button>

            {filtered.map((c) => {
              const selected = scope.mode === "one" && scope.competitionId === c.id;
              const excluded = scope.mode === "except" && scope.competitionId === c.id;
              const color = selected ? theme.accent : excluded ? theme.danger : theme.inkMuted;
              return (
                <div
                  key={c.id}
                  style={{ display: "flex", alignItems: "center", gap: 4, paddingRight: 6 }}
                >
                  {/* Click the name = SELECT this competition (green). */}
                  <button
                    type="button"
                    onClick={() => choose({ mode: "one", competitionId: c.id })}
                    style={{ ...optionStyle(color), flex: 1 }}
                  >
                    {c.name}
                  </button>
                  {/* Click the red ring-X = EXCLUDE this competition ("all except"). */}
                  <button
                    type="button"
                    aria-label={`Exclude ${c.name}`}
                    title={`All competitions except ${c.name}`}
                    onClick={() => choose({ mode: "except", competitionId: c.id })}
                    style={{
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      lineHeight: "16px",
                      textAlign: "center",
                      borderRadius: "50%",
                      border: `1px solid ${excluded ? theme.danger : "rgba(231,76,60,0.5)"}`,
                      background: "transparent",
                      color: excluded ? theme.danger : "rgba(231,76,60,0.6)",
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function optionStyle(color: string): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    color,
    fontSize: 11,
    padding: "6px 8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}
